/**
 * Zentrale Liste aller Anleitungen.
 * Neue Anleitung:
 * 1. Komponente in src/pages/guides/ anlegen (GuideLayout verwenden)
 * 2. Eintrag hier ergänzen (route, category, tags, Titel/Beschreibung in allen Sprachen, appliesTo)
 * 3. Route in App.tsx registrieren und in public/sitemap.xml eintragen
 *
 * Das Hilfe-Center gruppiert die Anleitungen nach `category`. Die Produktseite zeigt nur
 * Anleitungen, deren `appliesTo` zum Produkt passt.
 */
export const guideCategories = [
    {
        id: 'start',
        label: { en: 'Getting started', de: 'Erste Schritte', fr: 'Premiers pas', es: 'Primeros pasos', it: 'Primi passi', pt: 'Primeiros passos', nl: 'Aan de slag', pl: 'Pierwsze kroki', cs: 'První kroky', ja: 'はじめに', ko: '시작하기', zh: '入门', ru: 'Первые шаги', uk: 'Перші кроки', no: 'Kom i gang', sv: 'Kom igång' },
    },
    {
        id: 'purchase',
        label: { en: 'Buy & unlock', de: 'Kaufen & Freischalten', fr: 'Acheter et débloquer', es: 'Comprar y desbloquear', it: 'Acquisto e sblocco', pt: 'Comprar e desbloquear', nl: 'Kopen en ontgrendelen', pl: 'Zakup i odblokowanie', cs: 'Nákup a odemčení', ja: '購入とロック解除', ko: '구매 및 잠금 해제', zh: '购买与解锁', ru: 'Покупка и разблокировка', uk: 'Купівля та розблокування', no: 'Kjøp og lås opp', sv: 'Köp och lås upp' },
    },
    {
        id: 'data',
        label: { en: 'Set up data', de: 'Daten einrichten', fr: 'Configurer les données', es: 'Configurar datos', it: 'Configurare i dati', pt: 'Configurar dados', nl: 'Gegevens instellen', pl: 'Konfiguracja danych', cs: 'Nastavení dat', ja: 'データの設定', ko: '데이터 설정', zh: '数据设置', ru: 'Настройка данных', uk: 'Налаштування даних', no: 'Sett opp data', sv: 'Ställ in data' },
    },
    {
        id: 'customize',
        label: { en: 'Customize', de: 'Aussehen anpassen', fr: 'Personnaliser', es: 'Personalizar', it: 'Personalizzare', pt: 'Personalizar', nl: 'Aanpassen', pl: 'Personalizacja', cs: 'Přizpůsobení', ja: 'カスタマイズ', ko: '사용자 지정', zh: '个性化', ru: 'Настройка вида', uk: 'Налаштування вигляду', no: 'Tilpass', sv: 'Anpassa' },
    },
];
export const guidesRegistry = [
    // ---------- Erste Schritte ----------
    {
        id: 'install',
        route: '/guides/install',
        category: 'start',
        appliesTo: 'all',
        tags: ['install', 'installation', 'queue', 'select', 'activate', 'connect iq', 'installieren', 'auswählen', 'warteschlange'],
        title: {
            en: 'Install & select a watchface', de: 'Installieren & auswählen', fr: 'Installer et sélectionner', es: 'Instalar y seleccionar', it: 'Installare e selezionare', pt: 'Instalar e selecionar', nl: 'Installeren en selecteren', pl: 'Instalacja i wybór', cs: 'Instalace a výběr', ja: 'インストールと選択', ko: '설치 및 선택', zh: '安装与选择', ru: 'Установка и выбор', uk: 'Встановлення та вибір', no: 'Installer og velg', sv: 'Installera och välj',
        },
        description: {
            en: 'Install via the Connect IQ app, activate it on the watch and fix stuck installations',
            de: 'Über die Connect IQ App installieren, auf der Uhr aktivieren und hängende Installationen lösen',
            fr: 'Installer via l’app Connect IQ, l’activer sur la montre et débloquer une installation bloquée',
            es: 'Instalar con la app Connect IQ, activarla en el reloj y solucionar instalaciones atascadas',
            it: 'Installare con l’app Connect IQ, attivarlo sull’orologio e risolvere installazioni bloccate',
            pt: 'Instalar pela app Connect IQ, ativar no relógio e resolver instalações bloqueadas',
            nl: 'Installeren via de Connect IQ-app, activeren op het horloge en vastgelopen installaties oplossen',
            pl: 'Instalacja przez aplikację Connect IQ, aktywacja na zegarku i rozwiązywanie zawieszonych instalacji',
            cs: 'Instalace přes aplikaci Connect IQ, aktivace na hodinkách a řešení zaseknuté instalace',
            ja: 'Connect IQアプリでインストールし、時計で有効化。止まったインストールの対処',
            ko: 'Connect IQ 앱으로 설치하고 시계에서 활성화, 멈춘 설치 해결',
            zh: '通过 Connect IQ 应用安装、在手表上启用并解决安装卡住的问题',
            ru: 'Установка через приложение Connect IQ, активация на часах и решение зависших установок',
            uk: 'Встановлення через застосунок Connect IQ, активація на годиннику та вирішення завислих встановлень',
            no: 'Installer via Connect IQ-appen, aktiver på klokken og løs installasjoner som henger',
            sv: 'Installera via Connect IQ-appen, aktivera på klockan och lös installationer som fastnat',
        },
    },
    {
        id: 'settings',
        route: '/guides/settings',
        category: 'start',
        appliesTo: 'watchfaces',
        tags: ['settings', 'data field', 'options', 'n/a', 'save', 'einstellungen', 'datenfeld', 'optionen', 'speichern'],
        title: {
            en: 'Settings & data fields', de: 'Einstellungen & Datenfelder', fr: 'Réglages et champs de données', es: 'Ajustes y campos de datos', it: 'Impostazioni e campi dati', pt: 'Definições e campos de dados', nl: 'Instellingen en datavelden', pl: 'Ustawienia i pola danych', cs: 'Nastavení a datová pole', ja: '設定とデータフィールド', ko: '설정 및 데이터 필드', zh: '设置与数据字段', ru: 'Настройки и поля данных', uk: 'Налаштування та поля даних', no: 'Innstillinger og datafelt', sv: 'Inställningar och datafält',
        },
        description: {
            en: 'Open the settings in Connect IQ, all data field options explained, troubleshooting',
            de: 'Einstellungen in Connect IQ öffnen, alle Datenfeld-Optionen erklärt, Fehlerbehebung',
            fr: 'Ouvrir les réglages dans Connect IQ, toutes les options de champs expliquées, dépannage',
            es: 'Abrir los ajustes en Connect IQ, todas las opciones de campos explicadas, solución de problemas',
            it: 'Aprire le impostazioni in Connect IQ, tutte le opzioni dei campi spiegate, risoluzione problemi',
            pt: 'Abrir as definições no Connect IQ, todas as opções de campos explicadas, resolução de problemas',
            nl: 'Instellingen openen in Connect IQ, alle dataveldopties uitgelegd, probleemoplossing',
            pl: 'Otwieranie ustawień w Connect IQ, wszystkie opcje pól danych, rozwiązywanie problemów',
            cs: 'Otevření nastavení v Connect IQ, všechny možnosti datových polí, řešení potíží',
            ja: 'Connect IQで設定を開く、データフィールドの全オプション解説、トラブル対応',
            ko: 'Connect IQ에서 설정 열기, 모든 데이터 필드 옵션 설명, 문제 해결',
            zh: '在 Connect IQ 中打开设置、所有数据字段选项说明及故障排除',
            ru: 'Открытие настроек в Connect IQ, все варианты полей данных, решение проблем',
            uk: 'Відкриття налаштувань у Connect IQ, усі варіанти полів даних, усунення проблем',
            no: 'Åpne innstillingene i Connect IQ, alle datafeltvalg forklart, feilsøking',
            sv: 'Öppna inställningarna i Connect IQ, alla datafältsval förklarade, felsökning',
        },
    },
    {
        id: 'data-fields',
        route: '/guides/data-fields',
        category: 'start',
        appliesTo: 'watchfaces',
        tags: ['data field', 'datafield', 'options', 'tide', 'sunrise', 'golden hour', 'datenfeld', 'optionen', 'gezeiten', 'goldene stunde'],
        title: {
            en: 'Data field options',
            de: 'Datenfeld-Optionen',
            fr: 'Options des champs de données',
            es: 'Opciones de los campos de datos',
            it: 'Opzioni dei campi dati',
            pt: 'Opções dos campos de dados',
            nl: 'Gegevensveldopties',
            no: 'Alternativer for datafelt',
            sv: 'Datafältsalternativ',
            pl: 'Opcje pól danych',
            cs: 'Volby datových polí',
            uk: 'Опції полів даних',
            ru: 'Варианты полей данных',
            ja: 'データフィールドのオプション',
            ko: '데이터 필드 옵션',
            zh: '数据字段选项',
        },
        description: {
            en: 'Every value you can show in a data field, explained, with device availability',
            de: 'Jeder Wert für die Datenfelder erklärt, mit Verfügbarkeit je Uhr',
            fr: 'Chaque valeur affichable dans un champ de données, expliquée, avec la disponibilité par appareil',
            es: 'Cada valor que puedes mostrar en un campo de datos, explicado, con la disponibilidad por dispositivo',
            it: 'Ogni valore che puoi mostrare in un campo dati, spiegato, con la disponibilità per dispositivo',
            pt: 'Todos os valores que pode mostrar num campo de dados, explicados, com a disponibilidade por dispositivo',
            nl: 'Elke waarde die je in een gegevensveld kunt tonen, uitgelegd, met beschikbaarheid per apparaat',
            no: 'Hver verdi du kan vise i et datafelt, forklart, med tilgjengelighet per klokke',
            sv: 'Varje värde du kan visa i ett datafält, förklarat, med tillgänglighet per enhet',
            pl: 'Każda wartość, którą możesz pokazać w polu danych, z opisem i dostępnością na zegarkach',
            cs: 'Každá hodnota, kterou můžeš zobrazit v datovém poli, s vysvětlením a dostupností na zařízeních',
            uk: 'Кожне значення, яке можна показати в полі даних, з поясненням і доступністю на пристроях',
            ru: 'Каждое значение для поля данных с описанием и доступностью на разных часах',
            ja: 'データフィールドに表示できるすべての値の説明と、対応する時計',
            ko: '데이터 필드에 표시할 수 있는 모든 값의 설명과 사용 가능한 기기',
            zh: '可在数据字段中显示的每个数值及其说明，含各设备的可用性',
        },
    },
    {
        id: 'glossary',
        route: '/guides/glossary',
        category: 'start',
        appliesTo: 'all',
        tags: ['glossary', 'terms', 'watchfaceid', 'prokey', 'vx', 'elite', 'themestring', 'switchview', 'begriffe', 'glossar'],
        title: {
            en: 'Terms explained', de: 'Begriffe erklärt', fr: 'Lexique', es: 'Glosario', it: 'Glossario', pt: 'Glossário', nl: 'Begrippen uitgelegd', pl: 'Słowniczek', cs: 'Slovníček pojmů', ja: '用語集', ko: '용어 설명', zh: '术语说明', ru: 'Словарь терминов', uk: 'Словник термінів', no: 'Ordliste', sv: 'Ordlista',
        },
        description: {
            en: 'WatchfaceID, ProKey, VX, Elite, theme string, GPS fix, switchview and more',
            de: 'WatchfaceID, ProKey, VX, Elite, Themestring, GPS-Fix, Switchview und mehr',
            fr: 'WatchfaceID, ProKey, VX, Elite, chaîne de thème, fix GPS, switchview et plus',
            es: 'WatchfaceID, ProKey, VX, Elite, cadena de tema, fix GPS, switchview y más',
            it: 'WatchfaceID, ProKey, VX, Elite, stringa tema, fix GPS, switchview e altro',
            pt: 'WatchfaceID, ProKey, VX, Elite, string de tema, fix GPS, switchview e mais',
            nl: 'WatchfaceID, ProKey, VX, Elite, themastring, GPS-fix, switchview en meer',
            pl: 'WatchfaceID, ProKey, VX, Elite, ciąg motywu, fix GPS, switchview i więcej',
            cs: 'WatchfaceID, ProKey, VX, Elite, řetězec motivu, GPS fix, switchview a další',
            ja: 'WatchfaceID、ProKey、VX、Elite、テーマ文字列、GPS測位、スイッチビューなど',
            ko: 'WatchfaceID, ProKey, VX, Elite, 테마 문자열, GPS 수신, 스위치뷰 등',
            zh: 'WatchfaceID、ProKey、VX、Elite、主题字符串、GPS 定位、切换视图等',
            ru: 'WatchfaceID, ProKey, VX, Elite, строка темы, GPS-фиксация, switchview и другое',
            uk: 'WatchfaceID, ProKey, VX, Elite, рядок теми, GPS-фіксація, switchview та інше',
            no: 'WatchfaceID, ProKey, VX, Elite, temastreng, GPS-fix, switchview og mer',
            sv: 'WatchfaceID, ProKey, VX, Elite, temasträng, GPS-fix, switchview och mer',
        },
    },
    {
        id: 'watch-menu',
        route: '/guides/watch-menu',
        category: 'start',
        appliesTo: ['basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'iozen', 'vanguard', 'nightfever', 'blackhawk', 'zero', 'firmament'],
        tags: ['menu', 'style', 'info', 'theme', 'font', 'lock status', 'menü', 'schrift'],
        title: {
            en: 'Menu on the watch',
            de: 'Menü auf der Uhr',
            fr: 'Menu sur la montre',
            es: 'Menú en el reloj',
            it: 'Menu sull’orologio',
            pt: 'Menu no relógio',
            nl: 'Menu op het horloge',
            no: 'Meny på klokken',
            sv: 'Meny på klockan',
            pl: 'Menu na zegarku',
            cs: 'Menu na hodinkách',
            uk: 'Меню на годиннику',
            ru: 'Меню на часах',
            ja: '時計上のメニュー',
            ko: '시계의 메뉴',
            zh: '手表上的菜单',
        },
        description: {
            en: 'Change theme and fonts on the watch and check the Info page',
            de: 'Theme und Schriften auf der Uhr ändern, Info-Seite prüfen',
            fr: 'Changer le thème et les polices sur la montre et consulter la page Info',
            es: 'Cambiar el tema y las fuentes en el reloj y consultar la página Info',
            it: 'Cambia tema e caratteri sull’orologio e controlla la pagina Info',
            pt: 'Altere o tema e os tipos de letra no relógio e confira a página Info',
            nl: 'Thema en lettertypen op het horloge wijzigen en de Info-pagina bekijken',
            no: 'Endre tema og skrifter på klokken og sjekk Info-siden',
            sv: 'Ändra tema och typsnitt på klockan och kontrollera Info-sidan',
            pl: 'Zmiana motywu i czcionek na zegarku oraz sprawdzenie strony Info',
            cs: 'Změna motivu a písem na hodinkách a stránka Info',
            uk: 'Зміна теми й шрифтів на годиннику та сторінка Info',
            ru: 'Смена темы и шрифтов на часах и страница Info',
            ja: '時計でテーマとフォントを変更し、Infoページを確認する',
            ko: '시계에서 테마와 글꼴을 바꾸고 Info 페이지 확인하기',
            zh: '在手表上更改主题和字体并查看 Info 页面',
        },
    },
    {
        id: 'devices',
        route: '/guides/devices',
        category: 'start',
        appliesTo: 'all',
        tags: ['device', 'devices', 'mip', 'amoled', 'older', 'gradient', 'touch', 'gerät', 'ältere', 'farbverlauf'],
        title: {
            en: 'Watch models & differences',
            de: 'Uhrenmodelle & Unterschiede',
            fr: 'Modèles de montres et différences',
            es: 'Modelos de reloj y diferencias',
            it: 'Modelli di orologio e differenze',
            pt: 'Modelos de relógio e diferenças',
            nl: 'Horlogemodellen en verschillen',
            no: 'Klokkemodeller og forskjeller',
            sv: 'Klockmodeller och skillnader',
            pl: 'Modele zegarków i różnice',
            cs: 'Modely hodinek a rozdíly',
            uk: 'Моделі годинників і відмінності',
            ru: 'Модели часов и различия',
            ja: '時計のモデルと違い',
            ko: '시계 모델과 차이점',
            zh: '手表型号与差异',
        },
        description: {
            en: 'MIP and AMOLED, touch, gradients and what is missing on older watches',
            de: 'MIP und AMOLED, Touch, Farbverläufe und was auf älteren Uhren fehlt',
            fr: 'MIP et AMOLED, tactile, dégradés et ce qui manque sur les montres plus anciennes',
            es: 'MIP y AMOLED, táctil, degradados y lo que falta en los relojes más antiguos',
            it: 'MIP e AMOLED, touch, sfumature e cosa manca sugli orologi più vecchi',
            pt: 'MIP e AMOLED, toque, gradientes e o que falta nos relógios mais antigos',
            nl: 'MIP en AMOLED, touch, kleurverlopen en wat er op oudere horloges ontbreekt',
            no: 'MIP og AMOLED, berøring, fargeoverganger og hva som mangler på eldre klokker',
            sv: 'MIP och AMOLED, pekfunktion, gradienter och vad som saknas på äldre klockor',
            pl: 'MIP i AMOLED, dotyk, przejścia kolorów i czego brakuje na starszych zegarkach',
            cs: 'MIP a AMOLED, dotyk, přechody a co chybí na starších hodinkách',
            uk: 'MIP і AMOLED, сенсор, градієнти та що відсутнє на старіших годинниках',
            ru: 'MIP и AMOLED, сенсор, градиенты и то, чего не хватает на старых часах',
            ja: 'MIPとAMOLED、タッチ、グラデーション、古い時計で使えないもの',
            ko: 'MIP와 AMOLED, 터치, 그라데이션, 오래된 시계에서 사용할 수 없는 기능',
            zh: 'MIP 和 AMOLED、触控、渐变，以及较旧手表缺少的功能',
        },
    },
    // ---------- Kaufen & Freischalten ----------
    {
        id: 'purchase',
        route: '/guides/purchase',
        category: 'purchase',
        appliesTo: 'watchfaces',
        tags: ['purchase', 'buy', 'vx', 'elite', 'garmin pay', 'unlock', 'key', 'prokey', 'trial', 'zahlung', 'kauf', 'freischaltcode', 'testphase'],
        title: {
            en: 'VX or Elite? Buy & unlock', de: 'VX oder Elite? Kaufen & freischalten', fr: 'VX ou Elite ? Acheter et débloquer', es: '¿VX o Elite? Comprar y desbloquear', it: 'VX o Elite? Acquistare e sbloccare', pt: 'VX ou Elite? Comprar e desbloquear', nl: 'VX of Elite? Kopen en ontgrendelen', pl: 'VX czy Elite? Zakup i odblokowanie', cs: 'VX, nebo Elite? Nákup a odemčení', ja: 'VXかEliteか？購入とロック解除', ko: 'VX 또는 Elite? 구매 및 잠금 해제', zh: 'VX 还是 Elite？购买与解锁', ru: 'VX или Elite? Покупка и разблокировка', uk: 'VX чи Elite? Купівля та розблокування', no: 'VX eller Elite? Kjøp og lås opp', sv: 'VX eller Elite? Köp och lås upp',
        },
        description: {
            en: 'Differences between VX and Elite, buying an unlock key (ProKey) and what to do if it does not arrive',
            de: 'Unterschied zwischen VX und Elite, Freischaltcode (ProKey) kaufen und was tun, wenn er nicht ankommt',
            fr: 'Différences entre VX et Elite, achat d’une clé (ProKey) et que faire si elle n’arrive pas',
            es: 'Diferencias entre VX y Elite, comprar una clave (ProKey) y qué hacer si no llega',
            it: 'Differenze tra VX ed Elite, acquisto di una chiave (ProKey) e cosa fare se non arriva',
            pt: 'Diferenças entre VX e Elite, comprar uma chave (ProKey) e o que fazer se não chegar',
            nl: 'Verschil tussen VX en Elite, een sleutel (ProKey) kopen en wat te doen als die niet aankomt',
            pl: 'Różnice między VX i Elite, zakup klucza (ProKey) i co zrobić, gdy nie dotrze',
            cs: 'Rozdíl mezi VX a Elite, nákup klíče (ProKey) a co dělat, když nepřijde',
            ja: 'VXとEliteの違い、解除キー（ProKey）の購入、届かない場合の対処',
            ko: 'VX와 Elite의 차이, 잠금 해제 키(ProKey) 구매, 도착하지 않을 때',
            zh: 'VX 与 Elite 的区别、购买解锁密钥（ProKey）以及未收到时怎么办',
            ru: 'Разница между VX и Elite, покупка ключа (ProKey) и что делать, если он не пришёл',
            uk: 'Різниця між VX та Elite, купівля ключа (ProKey) і що робити, якщо він не прийшов',
            no: 'Forskjellen på VX og Elite, kjøp av opplåsingsnøkkel (ProKey) og hva du gjør hvis den ikke kommer',
            sv: 'Skillnaden mellan VX och Elite, köpa upplåsningsnyckel (ProKey) och vad du gör om den inte kommer',
        },
    },
    {
        id: 'find-watchface-id',
        route: '/guides/find-watchface-id',
        category: 'purchase',
        appliesTo: 'watchfaces',
        tags: ['watchfaceid', 'watchface id', 'id', 'key', 'prokey', 'lock status', 'info', 'schlüssel', 'finden', 'code eingeben'],
        title: {
            en: 'Find your WatchfaceID & enter the key', de: 'WatchfaceID finden & Code eingeben', fr: 'Trouver la WatchfaceID et saisir la clé', es: 'Encontrar la WatchfaceID e introducir la clave', it: 'Trovare la WatchfaceID e inserire la chiave', pt: 'Encontrar a WatchfaceID e introduzir a chave', nl: 'WatchfaceID vinden en sleutel invoeren', pl: 'Znajdź WatchfaceID i wpisz klucz', cs: 'Najít WatchfaceID a zadat klíč', ja: 'WatchfaceIDの確認とキー入力', ko: 'WatchfaceID 찾기 및 키 입력', zh: '查找 WatchfaceID 并输入密钥', ru: 'Найти WatchfaceID и ввести ключ', uk: 'Знайти WatchfaceID і ввести ключ', no: 'Finn WatchfaceID og skriv inn nøkkelen', sv: 'Hitta WatchfaceID och ange nyckeln',
        },
        description: {
            en: 'Where the WatchfaceID and the ProKey field are, and how to check the lock status on the watch',
            de: 'Wo WatchfaceID und ProKey-Feld stehen und wie du den Status auf der Uhr prüfst',
            fr: 'Où se trouvent la WatchfaceID et le champ ProKey, et comment vérifier le statut sur la montre',
            es: 'Dónde están la WatchfaceID y el campo ProKey, y cómo comprobar el estado en el reloj',
            it: 'Dove si trovano la WatchfaceID e il campo ProKey e come verificare lo stato sull’orologio',
            pt: 'Onde estão a WatchfaceID e o campo ProKey e como verificar o estado no relógio',
            nl: 'Waar de WatchfaceID en het ProKey-veld staan en hoe je de status op het horloge controleert',
            pl: 'Gdzie są WatchfaceID i pole ProKey oraz jak sprawdzić status na zegarku',
            cs: 'Kde najdeš WatchfaceID a pole ProKey a jak ověřit stav na hodinkách',
            ja: 'WatchfaceIDとProKey欄の場所、時計でのロック状態の確認方法',
            ko: 'WatchfaceID와 ProKey 필드 위치, 시계에서 잠금 상태 확인 방법',
            zh: 'WatchfaceID 和 ProKey 字段的位置，以及如何在手表上查看锁定状态',
            ru: 'Где найти WatchfaceID и поле ProKey и как проверить статус на часах',
            uk: 'Де знайти WatchfaceID і поле ProKey та як перевірити статус на годиннику',
            no: 'Hvor WatchfaceID og ProKey-feltet er, og hvordan du sjekker statusen på klokken',
            sv: 'Var WatchfaceID och ProKey-fältet finns och hur du kontrollerar statusen på klockan',
        },
    },
    // ---------- Daten einrichten ----------
    {
        id: 'weather',
        route: '/guides/weather',
        category: 'data',
        appliesTo: 'watchfaces',
        tags: ['weather', 'no data', 'waiting for data', 'openweathermap', 'owm', 'yr', 'open-meteo', 'openmeteo', 'visual crossing', 'api key', 'forecast', 'wetter', 'vorhersage'],
        title: {
            en: 'Weather provider setup', de: 'Wetteranbieter einrichten', fr: 'Configurer la météo', es: 'Configurar el proveedor del tiempo', it: 'Configurare il meteo', pt: 'Configurar o fornecedor de meteorologia', nl: 'Weerprovider instellen', pl: 'Konfiguracja dostawcy pogody', cs: 'Nastavení zdroje počasí', ja: '天気プロバイダーの設定', ko: '날씨 제공자 설정', zh: '天气数据源设置', ru: 'Настройка источника погоды', uk: 'Налаштування джерела погоди', no: 'Sett opp værleverandør', sv: 'Ställ in väderleverantör',
        },
        description: {
            en: 'Garmin, Yr, Open-Meteo, OpenWeatherMap and Visual Crossing: setup and what to do when it shows "no data"',
            de: 'Garmin, Yr, Open-Meteo, OpenWeatherMap und Visual Crossing: Einrichtung und was tun bei „no data“',
            fr: 'Garmin, Yr, Open-Meteo, OpenWeatherMap et Visual Crossing : configuration et que faire avec « no data »',
            es: 'Garmin, Yr, Open-Meteo, OpenWeatherMap y Visual Crossing: configuración y qué hacer con «no data»',
            it: 'Garmin, Yr, Open-Meteo, OpenWeatherMap e Visual Crossing: configurazione e cosa fare con «no data»',
            pt: 'Garmin, Yr, Open-Meteo, OpenWeatherMap e Visual Crossing: configuração e o que fazer com «no data»',
            nl: 'Garmin, Yr, Open-Meteo, OpenWeatherMap en Visual Crossing: instellen en wat te doen bij „no data“',
            pl: 'Garmin, Yr, Open-Meteo, OpenWeatherMap i Visual Crossing: konfiguracja i co robić przy „no data”',
            cs: 'Garmin, Yr, Open-Meteo, OpenWeatherMap a Visual Crossing: nastavení a co dělat při „no data“',
            ja: 'Garmin、Yr、Open-Meteo、OpenWeatherMap、Visual Crossingの設定と「no data」の対処',
            ko: 'Garmin, Yr, Open-Meteo, OpenWeatherMap, Visual Crossing 설정과 "no data" 해결',
            zh: 'Garmin、Yr、Open-Meteo、OpenWeatherMap 和 Visual Crossing 的设置以及出现“no data”时的处理',
            ru: 'Garmin, Yr, Open-Meteo, OpenWeatherMap и Visual Crossing: настройка и что делать при «no data»',
            uk: 'Garmin, Yr, Open-Meteo, OpenWeatherMap і Visual Crossing: налаштування та що робити при «no data»',
            no: 'Garmin, Yr, Open-Meteo, OpenWeatherMap og Visual Crossing: oppsett og hva du gjør ved «no data»',
            sv: 'Garmin, Yr, Open-Meteo, OpenWeatherMap och Visual Crossing: inställning och vad du gör vid ”no data”',
        },
    },
    {
        id: 'tides',
        route: '/guides/tides',
        category: 'data',
        appliesTo: ['horizon', 'mission', 'morphius', 'centurion', 'vega', 'io', 'victus', 'zenith', 'trio', 'basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'iozen', 'vanguard', 'nightfever', 'blackhawk', 'zero', 'firmament'],
        tags: ['tides', 'stormglass', 'api key', 'ocean', 'sea', 'coordinates', 'station', 'gezeiten', 'meer', 'koordinaten'],
        title: {
            en: 'Tides (Stormglass.io)', de: 'Gezeiten einrichten (Stormglass.io)', fr: 'Marées (Stormglass.io)', es: 'Mareas (Stormglass.io)', it: 'Maree (Stormglass.io)', pt: 'Marés (Stormglass.io)', nl: 'Getijden (Stormglass.io)', pl: 'Pływy (Stormglass.io)', cs: 'Příliv a odliv (Stormglass.io)', ja: '潮汐（Stormglass.io）', ko: '조석 (Stormglass.io)', zh: '潮汐（Stormglass.io）', ru: 'Приливы (Stormglass.io)', uk: 'Припливи (Stormglass.io)', no: 'Tidevann (Stormglass.io)', sv: 'Tidvatten (Stormglass.io)',
        },
        description: {
            en: 'Get a Stormglass API key, enter it and use alternative coordinates for inland locations',
            de: 'Stormglass-API-Key holen, eintragen und Ausweich-Koordinaten für Orte im Landesinneren nutzen',
            fr: 'Obtenir une clé API Stormglass, la saisir et utiliser des coordonnées alternatives à l’intérieur des terres',
            es: 'Obtener una clave API de Stormglass, introducirla y usar coordenadas alternativas en el interior',
            it: 'Ottenere una chiave API Stormglass, inserirla e usare coordinate alternative nell’entroterra',
            pt: 'Obter uma chave API Stormglass, introduzi-la e usar coordenadas alternativas no interior',
            nl: 'Een Stormglass-API-sleutel aanvragen, invoeren en alternatieve coördinaten gebruiken in het binnenland',
            pl: 'Uzyskanie klucza API Stormglass, wpisanie go i alternatywne współrzędne w głębi lądu',
            cs: 'Získat klíč API Stormglass, zadat ho a použít náhradní souřadnice ve vnitrozemí',
            ja: 'Stormglass APIキーの取得と入力、内陸での代替座標の使い方',
            ko: 'Stormglass API 키 발급과 입력, 내륙 지역용 대체 좌표',
            zh: '获取并输入 Stormglass API 密钥，内陆地区使用替代坐标',
            ru: 'Получить ключ API Stormglass, ввести его и задать альтернативные координаты вдали от моря',
            uk: 'Отримати ключ API Stormglass, ввести його та задати альтернативні координати вдалині від моря',
            no: 'Skaff en Stormglass-API-nøkkel, skriv den inn og bruk alternative koordinater i innlandet',
            sv: 'Skaffa en Stormglass-API-nyckel, ange den och använd alternativa koordinater i inlandet',
        },
    },
    {
        id: 'location',
        route: '/guides/location',
        category: 'data',
        appliesTo: 'watchfaces',
        tags: ['gps', 'gps?', 'location', 'fix', 'position', 'sunrise', 'sunset', 'standort', 'sonnenaufgang'],
        title: {
            en: 'GPS & location', de: 'GPS & Standort', fr: 'GPS et position', es: 'GPS y ubicación', it: 'GPS e posizione', pt: 'GPS e localização', nl: 'GPS en locatie', pl: 'GPS i lokalizacja', cs: 'GPS a poloha', ja: 'GPSと位置情報', ko: 'GPS 및 위치', zh: 'GPS 与位置', ru: 'GPS и местоположение', uk: 'GPS та місцезнаходження', no: 'GPS og posisjon', sv: 'GPS och position',
        },
        description: {
            en: 'Why the watchface needs a position, how to get a GPS fix and what "GPS?" means',
            de: 'Warum das Watchface einen Standort braucht, wie du einen GPS-Fix machst und was „GPS?“ bedeutet',
            fr: 'Pourquoi le cadran a besoin d’une position, comment obtenir un fix GPS et ce que signifie « GPS? »',
            es: 'Por qué la esfera necesita una posición, cómo obtener un fix GPS y qué significa «GPS?»',
            it: 'Perché il quadrante ha bisogno di una posizione, come ottenere un fix GPS e cosa significa «GPS?»',
            pt: 'Porque é que o mostrador precisa de uma posição, como obter um fix GPS e o que significa «GPS?»',
            nl: 'Waarom de watchface een positie nodig heeft, hoe je een GPS-fix krijgt en wat „GPS?“ betekent',
            pl: 'Dlaczego tarcza potrzebuje pozycji, jak uzyskać fix GPS i co oznacza „GPS?”',
            cs: 'Proč ciferník potřebuje polohu, jak získat GPS fix a co znamená „GPS?“',
            ja: 'ウォッチフェイスに位置が必要な理由、GPS測位の方法、「GPS?」の意味',
            ko: '워치페이스에 위치가 필요한 이유, GPS 수신 방법, "GPS?"의 의미',
            zh: '表盘为何需要位置、如何完成 GPS 定位以及“GPS?”的含义',
            ru: 'Зачем циферблату местоположение, как поймать GPS и что значит «GPS?»',
            uk: 'Навіщо циферблату місцезнаходження, як зловити GPS і що означає «GPS?»',
            no: 'Hvorfor urskiven trenger en posisjon, hvordan du får GPS-fix og hva «GPS?» betyr',
            sv: 'Varför urtavlan behöver en position, hur du får GPS-fix och vad ”GPS?” betyder',
        },
    },
    {
        id: 'timezones',
        route: '/guides/timezones',
        category: 'data',
        appliesTo: 'watchfaces',
        tags: ['timezone', 'time zone', 'utc', 'offset', 'second time', 'dst', 'daylight saving', 'zeitzone', 'sommerzeit'],
        title: {
            en: 'Time zones & UTC offset', de: 'Zeitzonen & UTC-Offset', fr: 'Fuseaux horaires et décalage UTC', es: 'Zonas horarias y desfase UTC', it: 'Fusi orari e offset UTC', pt: 'Fusos horários e desvio UTC', nl: 'Tijdzones en UTC-verschil', pl: 'Strefy czasowe i przesunięcie UTC', cs: 'Časová pásma a posun UTC', ja: 'タイムゾーンとUTCオフセット', ko: '시간대 및 UTC 오프셋', zh: '时区与 UTC 偏移', ru: 'Часовые пояса и смещение UTC', uk: 'Часові пояси та зсув UTC', no: 'Tidssoner og UTC-forskyvning', sv: 'Tidszoner och UTC-förskjutning',
        },
        description: {
            en: 'Second and third time zone in minutes, 12h/24h suffix and daylight saving time',
            de: 'Zweite und dritte Zeitzone in Minuten, 12-/24-h-Zusatz und Sommerzeit',
            fr: 'Deuxième et troisième fuseau en minutes, suffixe 12 h/24 h et heure d’été',
            es: 'Segunda y tercera zona en minutos, sufijo 12 h/24 h y horario de verano',
            it: 'Secondo e terzo fuso in minuti, suffisso 12/24 h e ora legale',
            pt: 'Segundo e terceiro fuso em minutos, sufixo 12 h/24 h e horário de verão',
            nl: 'Tweede en derde tijdzone in minuten, 12/24-uursachtervoegsel en zomertijd',
            pl: 'Druga i trzecia strefa w minutach, przyrostek 12/24 h i czas letni',
            cs: 'Druhé a třetí pásmo v minutách, přípona 12/24 h a letní čas',
            ja: '第2・第3タイムゾーン（分単位）、12/24時間の指定、夏時間',
            ko: '두 번째와 세 번째 시간대(분 단위), 12/24시간 접미사, 서머타임',
            zh: '以分钟设置第二、第三时区，12/24 小时后缀与夏令时',
            ru: 'Второй и третий пояс в минутах, суффикс 12/24 ч и летнее время',
            uk: 'Другий і третій пояс у хвилинах, суфікс 12/24 год і літній час',
            no: 'Andre og tredje tidssone i minutter, 12/24 t-tillegg og sommertid',
            sv: 'Andra och tredje tidszonen i minuter, 12/24 h-tillägg och sommartid',
        },
    },
    // ---------- Aussehen anpassen ----------
    {
        id: 'themes',
        route: '/guides/themes',
        category: 'customize',
        appliesTo: 'watchfaces',
        tags: ['theme', 'themestring', 'theme string', 'color', 'colour', 'sunset', 'farbe', 'farbschema', 'themestring'],
        title: {
            en: 'Themes & theme strings', de: 'Themes & Themestrings', fr: 'Thèmes et chaînes de thème', es: 'Temas y cadenas de tema', it: 'Temi e stringhe tema', pt: 'Temas e strings de tema', nl: 'Thema’s en themastrings', pl: 'Motywy i ciągi motywów', cs: 'Motivy a řetězce motivů', ja: 'テーマとテーマ文字列', ko: '테마와 테마 문자열', zh: '主题与主题字符串', ru: 'Темы и строки тем', uk: 'Теми та рядки тем', no: 'Temaer og temastrenger', sv: 'Teman och temasträngar',
        },
        description: {
            en: 'Save, share and apply color settings, switch the theme after sunset',
            de: 'Farbeinstellungen speichern, teilen und übernehmen, Theme nach Sonnenuntergang wechseln',
            fr: 'Enregistrer, partager et appliquer les couleurs, changer de thème après le coucher du soleil',
            es: 'Guardar, compartir y aplicar colores, cambiar de tema tras la puesta de sol',
            it: 'Salvare, condividere e applicare i colori, cambiare tema dopo il tramonto',
            pt: 'Guardar, partilhar e aplicar cores, mudar de tema após o pôr do sol',
            nl: 'Kleuren opslaan, delen en toepassen, thema wisselen na zonsondergang',
            pl: 'Zapisywanie, udostępnianie i stosowanie kolorów, zmiana motywu po zachodzie słońca',
            cs: 'Uložit, sdílet a použít barvy, přepnout motiv po západu slunce',
            ja: '色設定の保存・共有・適用、日没後のテーマ切り替え',
            ko: '색 설정 저장·공유·적용, 일몰 후 테마 전환',
            zh: '保存、分享和应用颜色设置，日落后切换主题',
            ru: 'Сохранять, делиться и применять цвета, менять тему после заката',
            uk: 'Зберігати, ділитися та застосовувати кольори, змінювати тему після заходу сонця',
            no: 'Lagre, dele og bruke farger, bytte tema etter solnedgang',
            sv: 'Spara, dela och använda färger, byta tema efter solnedgång',
        },
    },
    {
        id: 'date-format',
        route: '/guides/date-format',
        category: 'customize',
        appliesTo: 'watchfaces',
        tags: ['date', 'date format', 'format code', 'day', 'month', 'week', 'datum', 'datumsformat', 'kalenderwoche'],
        title: {
            en: 'Custom date format', de: 'Eigenes Datumsformat', fr: 'Format de date personnalisé', es: 'Formato de fecha personalizado', it: 'Formato data personalizzato', pt: 'Formato de data personalizado', nl: 'Eigen datumnotatie', pl: 'Własny format daty', cs: 'Vlastní formát data', ja: '日付形式のカスタマイズ', ko: '사용자 지정 날짜 형식', zh: '自定义日期格式', ru: 'Свой формат даты', uk: 'Власний формат дати', no: 'Eget datoformat', sv: 'Eget datumformat',
        },
        description: {
            en: 'Format codes for Date 1/2/3 with examples',
            de: 'Formatcodes für Datum 1/2/3 mit Beispielen',
            fr: 'Codes de format pour Date 1/2/3 avec exemples',
            es: 'Códigos de formato para Fecha 1/2/3 con ejemplos',
            it: 'Codici di formato per Data 1/2/3 con esempi',
            pt: 'Códigos de formato para Data 1/2/3 com exemplos',
            nl: 'Formaatcodes voor Datum 1/2/3 met voorbeelden',
            pl: 'Kody formatu dla Daty 1/2/3 z przykładami',
            cs: 'Formátovací kódy pro Datum 1/2/3 s příklady',
            ja: '日付1/2/3のフォーマットコードと例',
            ko: '날짜 1/2/3 형식 코드와 예시',
            zh: '日期 1/2/3 的格式代码及示例',
            ru: 'Коды формата для Даты 1/2/3 с примерами',
            uk: 'Коди формату для Дати 1/2/3 з прикладами',
            no: 'Formatkoder for Dato 1/2/3 med eksempler',
            sv: 'Formatkoder för Datum 1/2/3 med exempel',
        },
    },
    {
        id: 'fonts',
        route: '/guides/fonts',
        category: 'customize',
        appliesTo: 'watchfaces',
        tags: ['font', 'fonts', 'time font', 'data font', 'schrift', 'schriftart'],
        title: {
            en: 'Fonts', de: 'Schriften', fr: 'Polices', es: 'Fuentes', it: 'Caratteri', pt: 'Fontes', nl: 'Lettertypen', pl: 'Czcionki', cs: 'Písma', ja: 'フォント', ko: '글꼴', zh: '字体', ru: 'Шрифты', uk: 'Шрифти', no: 'Skrifttyper', sv: 'Typsnitt',
        },
        description: {
            en: 'Time and data fonts at a glance',
            de: 'Zeit- und Datenschriften im Überblick',
            fr: 'Aperçu des polices de l’heure et des données',
            es: 'Fuentes de hora y de datos de un vistazo',
            it: 'Panoramica dei caratteri per ora e dati',
            pt: 'Fontes de hora e de dados num relance',
            nl: 'Tijd- en datalettertypen in één oogopslag',
            pl: 'Czcionki czasu i danych w skrócie',
            cs: 'Přehled písem pro čas a data',
            ja: '時刻とデータのフォント一覧',
            ko: '시간 및 데이터 글꼴 한눈에 보기',
            zh: '时间与数据字体一览',
            ru: 'Шрифты времени и данных',
            uk: 'Шрифти часу та даних',
            no: 'Skrifttyper for tid og data',
            sv: 'Typsnitt för tid och data',
        },
    },
    {
        id: 'switchviews',
        route: '/guides/switchviews',
        category: 'customize',
        appliesTo: ['horizon', 'mission', 'dinstinct', 'morphius', 'centurion', 'vega', 'io', 'victus', 'zenith', 'trio', 'basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'vanguard', 'nightfever', 'blackhawk', 'zero', 'firmament'],
        tags: ['switchview', 'switch view', 'gesture', 'wrist', 'second gesture', 'long press', 'touch', 'geste', 'handgelenk', 'wechselansicht', 'lange drücken'],
        title: {
            en: 'Switchviews & gestures', de: 'Switchviews & Gesten', fr: 'Switchviews et gestes', es: 'Switchviews y gestos', it: 'Switchview e gesti', pt: 'Switchviews e gestos', nl: 'Switchviews en gebaren', pl: 'Switchview i gesty', cs: 'Switchview a gesta', ja: 'スイッチビューとジェスチャー', ko: '스위치뷰와 제스처', zh: '切换视图与手势', ru: 'Switchview и жесты', uk: 'Switchview та жести', no: 'Switchviews og bevegelser', sv: 'Switchviews och gester',
        },
        description: {
            en: 'Switch views with a second wrist movement or a long press on touch watches',
            de: 'Ansichten per zweiter Handgelenksbewegung oder langem Drücken auf Touch-Uhren wechseln',
            fr: 'Changer de vue par un second mouvement du poignet ou un appui long sur les montres tactiles',
            es: 'Cambiar de vista con un segundo giro de muñeca o una pulsación larga en relojes táctiles',
            it: 'Cambiare vista con un secondo movimento del polso o una pressione prolungata sugli orologi touch',
            pt: 'Mudar de vista com um segundo movimento do pulso ou um toque longo nos relógios táteis',
            nl: 'Weergaven wisselen met een tweede polsbeweging of lang indrukken op touchhorloges',
            pl: 'Przełączanie widoków drugim ruchem nadgarstka lub długim naciśnięciem na zegarkach dotykowych',
            cs: 'Přepínání pohledů druhým pohybem zápěstí nebo dlouhým stiskem na dotykových hodinkách',
            ja: '2回目の手首の動き、またはタッチ対応時計での長押しで表示を切り替え',
            ko: '두 번째 손목 동작이나 터치 시계의 길게 누르기로 화면 전환',
            zh: '通过第二次抬腕或在触屏手表上长按来切换视图',
            ru: 'Переключение видов вторым движением запястья или долгим нажатием на сенсорных часах',
            uk: 'Перемикання видів другим рухом зап’ястя або довгим натисканням на сенсорних годинниках',
            no: 'Bytt visning med en ny håndleddsbevegelse eller langt trykk på berøringsklokker',
            sv: 'Byt vy med en andra handledsrörelse eller långt tryck på pekskärmsklockor',
        },
    },
    {
        id: 'touch',
        route: '/guides/touch',
        category: 'customize',
        appliesTo: ['basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'iozen', 'vanguard', 'nightfever', 'blackhawk', 'zero'],
        tags: ['touch', 'long press', 'tap', 'open apps', 'langes drücken', 'tippen', 'apps öffnen'],
        title: {
            en: 'Long press & touch',
            de: 'Langes Drücken & Touch',
            fr: 'Appui long et tactile',
            es: 'Pulsación larga y táctil',
            it: 'Pressione prolungata e touch',
            pt: 'Toque prolongado e ecrã tátil',
            nl: 'Lang indrukken en touch',
            no: 'Langt trykk og berøring',
            sv: 'Långt tryck och pekfunktion',
            pl: 'Długie naciśnięcie i dotyk',
            cs: 'Dlouhé stisknutí a dotyk',
            uk: 'Тривале натискання й сенсор',
            ru: 'Долгое нажатие и сенсор',
            ja: '長押しとタッチ',
            ko: '길게 누르기와 터치',
            zh: '长按与触控',
        },
        description: {
            en: 'Long-press actions on time and icons, opening Garmin apps from data fields',
            de: 'Aktionen bei langem Drücken auf Zeit und Icons, Garmin-Apps aus Datenfeldern öffnen',
            fr: 'Actions d’appui long sur l’heure et les icônes, ouverture d’apps Garmin depuis les champs de données',
            es: 'Acciones de pulsación larga en la hora y los iconos, apertura de apps de Garmin desde los campos de datos',
            it: 'Azioni della pressione prolungata su ora e icone, apertura delle app Garmin dai campi dati',
            pt: 'Ações do toque prolongado na hora e nos ícones, abrir apps Garmin a partir de campos de dados',
            nl: 'Acties bij lang indrukken op tijd en pictogrammen, Garmin-apps openen vanuit gegevensvelden',
            no: 'Handlinger ved langt trykk på klokkeslett og ikoner, åpning av Garmin-apper fra datafelt',
            sv: 'Åtgärder vid långt tryck på tid och ikoner, öppna Garmin-appar från datafält',
            pl: 'Akcje długiego naciśnięcia na czasie i ikonach, otwieranie aplikacji Garmin z pól danych',
            cs: 'Akce dlouhého stisknutí na času a ikonách, otevírání aplikací Garmin z datových polí',
            uk: 'Дії тривалого натискання на часі та іконках, відкриття програм Garmin з полів даних',
            ru: 'Действия по долгому нажатию на время и значки, открытие приложений Garmin из полей данных',
            ja: '時刻とアイコンの長押しアクション、データフィールドからのGarminアプリの起動',
            ko: '시간과 아이콘의 길게 누르기 동작, 데이터 필드에서 Garmin 앱 열기',
            zh: '对时间和图标的长按操作，从数据字段打开 Garmin 应用',
        },
    },
    {
        id: 'display-modes',
        route: '/guides/display-modes',
        category: 'customize',
        appliesTo: ['basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'iozen', 'vanguard', 'nightfever', 'blackhawk', 'zero'],
        tags: ['aod', 'always on', 'idle', 'low power', 'power saving', 'battery', 'mip', 'amoled', 'hands', 'energiesparen', 'akku', 'ruhezustand', 'zeiger'],
        title: {
            en: 'Always-on & power saving',
            de: 'Always-on & Energiesparen',
            fr: 'Always-on et économie d’énergie',
            es: 'Siempre activa y ahorro de energía',
            it: 'Sempre attivo e risparmio energetico',
            pt: 'Sempre ligado e poupança de energia',
            nl: 'Always-on en energie besparen',
            no: 'Alltid på og strømsparing',
            sv: 'Alltid på och strömsparande',
            pl: 'Always-on i oszczędzanie energii',
            cs: 'Vždy zapnuto a úspora energie',
            uk: 'Завжди ввімкнено та енергозбереження',
            ru: 'Always On и энергосбережение',
            ja: '常時表示と省電力',
            ko: '상시 표시와 절전',
            zh: '常亮与节电',
        },
        description: {
            en: 'Idle and AOD options, power-saving modes on MIP watches, hands on the Instinct Crossover',
            de: 'Ruhezustand und AOD, Energiesparmodi auf MIP-Uhren, Zeiger der Instinct Crossover',
            fr: 'Options de veille et d’AOD, modes d’économie d’énergie sur les montres MIP, aiguilles de l’Instinct Crossover',
            es: 'Opciones de reposo y AOD, modos de ahorro de energía en relojes MIP, manecillas del Instinct Crossover',
            it: 'Opzioni di inattività e AOD, modalità di risparmio energetico sugli orologi MIP, lancette sull’Instinct Crossover',
            pt: 'Opções de inatividade e AOD, modos de poupança de energia nos relógios MIP, ponteiros no Instinct Crossover',
            nl: 'Opties voor inactiviteit en AOD, energiebesparende modi op MIP-horloges, wijzers op de Instinct Crossover',
            no: 'Alternativer for hvile og AOD, strømsparemodus på MIP-klokker, viserne på Instinct Crossover',
            sv: 'Alternativ för viloläge och AOD, strömsparlägen på MIP-klockor, visare på Instinct Crossover',
            pl: 'Opcje bezczynności i AOD, tryby oszczędzania energii na zegarkach MIP, wskazówki w Instinct Crossover',
            cs: 'Volby nečinnosti a AOD, úsporné režimy na hodinkách MIP, ručičky na Instinct Crossover',
            uk: 'Параметри очікування й AOD, енергоощадні режими на годинниках MIP, стрілки на Instinct Crossover',
            ru: 'Параметры бездействия и AOD, режимы энергосбережения на часах с MIP, стрелки на Instinct Crossover',
            ja: '待機とAODのオプション、MIPの時計の省電力モード、Instinct Crossoverの針',
            ko: '대기 및 AOD 옵션, MIP 시계의 절전 모드, Instinct Crossover의 시곗바늘',
            zh: '空闲和 AOD 选项、MIP 手表的节电模式、Instinct Crossover 的指针',
        },
    },
];
/** Prüft, ob eine Anleitung für ein Produkt gilt. */
export function guideAppliesTo(guide, productSlug, isWatchface) {
    if (guide.appliesTo === 'all')
        return true;
    if (guide.appliesTo === 'watchfaces')
        return isWatchface;
    return guide.appliesTo.includes(productSlug);
}
export function getGuideByRoute(route) {
    return guidesRegistry.find((g) => g.route === route);
}
export function localized(record, lang) {
    return record[lang] || record['en'] || '';
}
export function searchGuides(query, language = 'en') {
    if (!query.trim())
        return [];
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return guidesRegistry.filter((guide) => {
        const title = localized(guide.title, language).toLowerCase();
        const description = localized(guide.description, language).toLowerCase();
        const tags = guide.tags.map((t) => t.toLowerCase());
        return terms.some((term) => title.includes(term) ||
            description.includes(term) ||
            tags.some((tag) => tag.includes(term) || (term.length > 2 && term.includes(tag))));
    });
}
